import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { PaintSmear } from "@/components/site/PaintSmear";
import { BUILD_REFUND_PERCENT } from "@/lib/commercial-terms";

const steps = [
  {
    n: "01",
    label: "Build",
    title: "Your team, built and launched",
    body: "One payment covers the people, content, outreach and CRM setup to start selling.",
    refund: `${BUILD_REFUND_PERCENT}% of your build payment back if we miss the agreed target.`,
  },
  {
    n: "02",
    label: "Prove",
    title: "$0 a month until we hit your target",
    body: "For example: $80k collected from new customers. We agree your target before you pay. Until we hit it, you pay commission only.",
  },
  {
    n: "03",
    label: "Scale",
    title: "Choose to grow the team",
    body: "Target hit? You can pay monthly for more salespeople, more campaigns and more customers. It's your choice.",
  },
];

export const HowItWorks = () => {
  const listRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: listRef,
    offset: ["start 0.75", "end 0.35"],
  });
  const lineHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section id="how" className="relative scroll-mt-20 overflow-hidden bg-paper border-t border-ink" data-testid="how-section">
      <PaintSmear className="absolute top-2 right-[-120px] w-[240px] opacity-35 md:top-4 md:right-[-160px] md:w-[380px] md:opacity-55" />
      <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 py-10 md:py-14 lg:grid lg:grid-cols-12 lg:gap-12">
        <div className="lg:col-span-5 lg:sticky lg:top-28 lg:self-start lg:pb-8">
          <p className="mb-4 font-mono text-base md:text-lg font-semibold uppercase tracking-[0.14em] text-flame text-pretty">
            How you pay
          </p>
          <h2 className="font-display font-semibold tracking-[-0.02em] text-4xl md:text-6xl lg:text-7xl leading-[0.92] text-ink max-w-xl text-balance">
            Pay once. Then commission.{" "}
            <span className="font-editorial italic font-medium text-cobalt">Then monthly, if you scale.</span>
          </h2>
          <p className="mt-5 text-xl md:text-2xl font-light leading-relaxed text-ink max-w-md text-pretty">
            See paying customers before you choose a monthly commitment.
          </p>
        </div>

        <div ref={listRef} className="relative mt-8 lg:mt-0 lg:col-span-7 border-t border-ink">
          <div className="hidden md:block pointer-events-none absolute left-[2.65rem] top-0 bottom-0 w-px bg-ink/15" aria-hidden />
          <motion.div
            className="hidden md:block pointer-events-none absolute left-[2.65rem] top-0 w-px origin-top bg-cobalt"
            style={{ height: lineHeight }}
            aria-hidden
          />

          {steps.map((s) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="group relative border-b border-ink py-6 md:py-8"
              data-testid={`how-step-${s.n}`}
            >
              <div className="flex items-baseline gap-5 md:gap-8">
                <span className="relative z-10 font-editorial italic text-4xl md:text-6xl leading-none text-stroke w-[3rem] md:w-24 shrink-0 bg-paper group-hover:[-webkit-text-stroke-color:#1B36F9] transition-all duration-300">
                  {s.n}
                </span>
                <div>
                  <p className="mb-2 font-mono text-sm md:text-base font-semibold uppercase tracking-[0.14em] text-flame text-pretty">
                    {s.label}
                  </p>
                  <h3 className="font-display text-2xl md:text-4xl font-medium tracking-tight text-ink text-balance">{s.title}</h3>
                </div>
              </div>
              <p className="mt-4 md:pl-32 text-xl md:text-2xl font-light text-ink leading-relaxed max-w-2xl text-pretty">
                {s.body}
              </p>
              {s.refund && <p className="mt-4 md:ml-32 border-l-4 border-flame pl-4 text-lg md:text-xl font-semibold leading-snug max-w-xl">{s.refund}</p>}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
